import { ActionItem, UserRef } from '@urbanmix-tech/shared-js';
import { saveActionItem } from '@/data';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import { useAuth } from '@/shared/context/AuthProvider';
import { convertPseudoDateToRealDate } from '@/helpers/common';
import { useAlert } from '@/contexts/Alert/AlertContext';

export type TaskEditDialogProps = {
  actionItem: ActionItem;
  open: boolean;
  onClose: () => void;
  onSave?: () => void;
};

export function TaskEditDialog({
  actionItem,
  open,
  onClose,
  onSave,
}: TaskEditDialogProps) {
  const authContext = useAuth();
  const { usersData } = authContext;
  const { showAlert } = useAlert();

  // Convert all users to array for selection
  const allUsers = Object.values(usersData);

  // Initialize state with current values
  const [dueDate, setDueDate] = useState<Dayjs | null>(
    actionItem.dueDate ? dayjs(convertPseudoDateToRealDate(actionItem.dueDate)) : null
  );
  const [selectedOwnerId, setSelectedOwnerId] = useState<string>(
    actionItem.owner?.userId || ''
  );
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    try {
      setIsSaving(true);

      const updatedItem = {
        ...actionItem,
        dueDate: dueDate ? dueDate.toDate() : actionItem.dueDate,
        owner: selectedOwnerId
          ? {
              userId: selectedOwnerId,
              email: usersData[selectedOwnerId]?.email || '',
            }
          : undefined,
      } as ActionItem;

      await saveActionItem(updatedItem);
      showAlert('Task updated successfully', 'success');
      onSave?.();
      onClose();
    } catch (error) {
      console.error('Error saving task:', error);
      showAlert('Failed to update task', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Task</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          <div>
            <div className="text-sm font-semibold text-zinc-300 mb-2">
              Task Name
            </div>
            <div className="text-sm text-zinc-400">{actionItem.title}</div>
          </div>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Due Date"
              value={dueDate}
              onChange={(newValue) => setDueDate(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'outlined',
                },
              }}
            />
          </LocalizationProvider>

          <FormControl fullWidth variant="outlined">
            <InputLabel id="owner-select-label">Owner</InputLabel>
            <Select
              labelId="owner-select-label"
              id="owner-select"
              value={selectedOwnerId}
              onChange={(e) => setSelectedOwnerId(e.target.value)}
              label="Owner"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {allUsers.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.fullName || user.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
